import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

import styles from './Input.module.css';

type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'className'
>;

export interface InputProps extends NativeInputProps {
  label?: string;
  value: string;
  /** Отдаёт уже готовую строку, а не событие: экраны не работают с DOM. */
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  hint?: string;
  type?: 'text' | 'email' | 'tel' | 'password' | 'search' | 'url';
  suffix?: ReactNode;
  className?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    value,
    onChange,
    placeholder,
    error,
    hint,
    type = 'text',
    inputMode,
    suffix,
    autoFocus,
    maxLength,
    disabled,
    className,
    id,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className={cn(styles.field, disabled && styles.disabled, className)}>
      {label !== undefined ? (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      ) : null}

      <div className={cn(styles.shell, error !== undefined && styles.invalid)}>
        <input
          {...rest}
          ref={ref}
          id={inputId}
          className={styles.input}
          type={type}
          inputMode={inputMode}
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={describedBy}
          autoComplete={rest.autoComplete ?? 'off'}
          onChange={(event) => onChange(event.target.value)}
        />
        {suffix !== undefined ? <span className={styles.suffix}>{suffix}</span> : null}
      </div>

      {error !== undefined ? (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      ) : hint !== undefined ? (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
});
